/**
 * Поповер выбора папки-платья: «В папку ▾» на панели выделения и «Слить с… ▾»
 * в шапке папки. Поиск по имени (и по «Платье #id» у безымянной), у каждой
 * папки — сколько в ней фото; Enter берёт единственное совпадение.
 *
 * `allowNew` — кнопка «Новая папка» (`onPick(null)`): перенос в новую папку
 * есть, а слияния с новой — нет, это тот же перенос.
 *
 * Esc и клик мимо закрывают (`usePopoverClose`): Esc дальше не идёт и
 * выделение не снимает.
 */
import { FolderPlus } from "lucide-react";
import { useMemo, useRef, useState, type FormEvent } from "react";

import { input } from "@/components/trends/ui";
import { usePopoverClose } from "@/components/trends/TagPicker";
import { num } from "@/lib/media";
import { dressTitle, type DressCard } from "@/lib/photos";

export function DressPicker({
  dresses,
  exclude,
  allowNew,
  onPick,
  onClose,
  up = false,
  annotate,
}: {
  dresses: DressCard[];
  /** Папка, которой в списке быть не должно — обычно текущая. */
  exclude: number | null;
  allowNew: boolean;
  /** `null` — новая папка. */
  onPick: (dressId: number | null) => void;
  onClose: () => void;
  /** Раскрыться вверх: над плавающей панелью внизу экрана. */
  up?: boolean;
  /** Бейдж после названия папки: «уже было с #5». */
  annotate?: ReadonlyMap<number, string>;
}) {
  const box = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState("");

  usePopoverClose(box, onClose);

  const needle = query.trim().toLowerCase();
  const list = useMemo(
    () => dresses.filter((d) => d.id !== exclude && (!needle || dressTitle(d).toLowerCase().includes(needle))),
    [dresses, exclude, needle],
  );

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (list.length === 1) onPick(list[0]!.id);
  };

  return (
    <div
      ref={box}
      className={`absolute left-0 z-10 grid w-[280px] gap-1.5 rounded-[var(--radius-card)] border border-border bg-bg p-2 shadow-xl ${
        up ? "bottom-full mb-2" : "top-full mt-2"
      }`}
      data-annot="выбор папки"
    >
      <form onSubmit={submit}>
        <input
          autoFocus
          className={`${input} w-full`}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          maxLength={160}
          placeholder="найти папку… ⏎"
        />
      </form>
      <div className="max-h-[260px] overflow-auto">
        {list.length === 0 ? (
          <div className="px-1.5 py-2 text-[12px] text-fg-faint">
            {needle ? "Нет совпадений." : "Других папок нет."}
          </div>
        ) : (
          list.map((d) => (
            <button
              key={d.id}
              type="button"
              onClick={() => onPick(d.id)}
              className="flex w-full items-center gap-2 rounded-[var(--radius-ui)] px-1.5 py-1 text-left text-[12.5px] hover:bg-bg-hover"
            >
              <span className={`min-w-0 truncate ${d.name ? "text-fg" : "text-fg-muted"}`}>{dressTitle(d)}</span>
              {annotate?.get(d.id) ? (
                <span className="flex-none text-[11px] text-warn">{annotate.get(d.id)}</span>
              ) : null}
              <span className="ml-auto flex-none text-[11px] tnum text-fg-faint">{num(d.count)} фото</span>
            </button>
          ))
        )}
      </div>
      {allowNew ? (
        <button
          type="button"
          onClick={() => onPick(null)}
          className="inline-flex items-center gap-1 rounded-[var(--radius-ui)] border border-dashed border-border px-1.5 py-1 text-left text-[12px] text-accent-text hover:bg-bg-hover"
        >
          <FolderPlus size={12} />
          Новая папка
        </button>
      ) : null}
    </div>
  );
}
